import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaHeart,
  FaRegHeart,
  FaCheckCircle,
  FaInfoCircle,
  FaUniversity,
  FaUserTie,
  FaCalendarAlt,
  FaEye,
  FaMapMarkerAlt,
  FaGraduationCap
} from "react-icons/fa";
import useAnalytics from "../../hooks/useAnalytics";
import { OpportunityService } from "../../services/OpportunityService";
import { encodeImageUrl, getApiImageUrl } from "../../utils/helpers";

// Tooltip component for icons
const IconWithTooltip = ({ imgSrc, icon: Icon, label, color, className, position }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={label}
            className={`${color} ${className || "h-6 w-6"} object-contain`}
          />
        ) : Icon ? (
          <Icon className={`${color} ${className || "h-6 w-6"}`} />
        ) : null}
      </div>
      {showTooltip && (
        <div
          className={`absolute ${position === "right" ? "right-full" : "left-full"} ml-2 top-1/2 transform -translate-y-1/2 bg-[#1B222A] text-white text-sm rounded py-1 px-2 whitespace-nowrap z-10`}
        >
          {label}
        </div>
      )}
    </div>
  );
};

// Opportunity card component - Redesigned for horizontal layout
const OpportunityCard = ({
  opportunity,
  user,
  savedOpportunities = new Set(),
  handleToggleSave,
}) => {
  const [imgError, setImgError] = useState(false);
  const navigate = useNavigate();
  const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "G-XXXXXXXXXX";
  const { trackEvent } = useAnalytics(GA_MEASUREMENT_ID);

  const {
    id,
    university,
    title,
    department,
    supervisor,
    supervisor_url,
    closing_date,
    open_to,
    city,
    country,
    opportunity_url,
    fully_funded,
    ai_summary,
    created_at
  } = opportunity;

  const ageInfo = useMemo(() => {
    if (!created_at) return null;
    const createdAtEpochSeconds = parseInt(created_at, 10);
    if (isNaN(createdAtEpochSeconds)) return null;

    const createdAtEpochMs = createdAtEpochSeconds * 1000;
    const now = new Date();

    // Calculate two days back at 12 AM UTC
    const twoDaysBack = new Date(now);
    twoDaysBack.setUTCDate(now.getUTCDate() - 2);
    twoDaysBack.setUTCHours(0, 0, 0, 0);
    const twoDaysBackCutoff = twoDaysBack.getTime();

    // Calculate 3 and 7 days ago in milliseconds
    const currentTimeMs = now.getTime();
    const threeDaysAgoMs = currentTimeMs - 3 * 24 * 3600 * 1000;
    const sevenDaysAgoMs = currentTimeMs - 7 * 24 * 3600 * 1000;

    if (createdAtEpochMs >= twoDaysBackCutoff) {
      return {
        label: "Latest",
        bgColor: "bg-[#28a745]",
        textColor: "text-white",
      };
    } else if (
      createdAtEpochMs > sevenDaysAgoMs &&
      createdAtEpochMs <= threeDaysAgoMs
    ) {
      return {
        label: "One week old",
        bgColor: "bg-[#ffc107]",
        textColor: "text-[#1B222A]",
      };
    }
    return null;
  }, [created_at]);

  useEffect(() => {
    if (opportunity && opportunity.university) {
    const img = new Image();
    img.src = getApiImageUrl('university', `${opportunity.university}.png`);
    img.onload = () => setImgError(false);
    img.onerror = () => setImgError(true);
    }
  }, [opportunity]);

  const opportunityLink = OpportunityService.generateOpportunityLink(opportunity);
  const handleClick = () => {
    trackEvent({
      category: "Opportunity",
      action: "View Opportunity Details",
      label: opportunity.title,
    });
    // Open in new tab while preserving current filters
    const currentUrl = window.location.href;
    const opportunityUrl = `${window.location.origin}/opportunities/${opportunityLink}`;
    window.open(opportunityUrl, '_blank');
  };

  const handleSaveToggle = () => {
    if (user) {
      handleToggleSave(opportunity.id);
      trackEvent({
        category: "Opportunity",
        action: savedOpportunities.has(opportunity.id) ? "Unsave Opportunity" : "Save Opportunity",
        label: opportunity.title,
      });
    }
  };

  if (!opportunity) {
    return (
      <div className="bg-gradient-to-br from-[#FFEEED] to-[#FFD1CE] rounded-xl shadow-md p-6 flex items-center justify-center h-full">
        <p className="text-[#1B222A]">Opportunity information not available</p>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 group relative ${window.innerWidth < 768 ? 'mx-2 border-0' : 'mx-[5%] border border-gray-100 hover:border-[#DB4F40]/20'}`}>
      <div className="p-6">
        <div className="flex flex-col lg:flex-row lg:items-stretch gap-6 min-h-[180px]">
          {/* Left Section - University Logo */}
          <div className="flex-shrink-0 flex flex-col justify-center items-center h-full lg:h-auto lg:justify-center bg-gradient-to-b from-[#FFEEED] to-[#FFD6D4] rounded-xl" style={{ flex: '0 0 15%' }}>
            <div className="w-48 h-48 lg:w-42 lg:h-42 flex-shrink-0 flex items-center justify-center">
              <img
                src={getApiImageUrl('university', `${opportunity.university}.png`)}
                alt={university}
                className="w-full h-full object-contain transition-opacity duration-300 hover:opacity-80"
                onError={() => setImgError(true)}
              />
            </div>
          </div>

          {/* Center Section - Main Content */}
          <div className="flex-1 min-w-0" style={{ flex: '0 0 70%' }}>
            {/* Header with title and badges */}
            <div className="mb-4">
              <div className="flex items-start mb-3 justify-between">
                <h3 className="text-lg font-bold text-[#1B222A] hover:text-[#DB4F40] transition-colors cursor-pointer group-hover:text-[#DB4F40] flex-1 mr-4 lg:max-w-[85%] max-w-none lg:line-clamp-2 leading-tight" onClick={handleClick}>
                  {title}
                </h3>
                {/* Heart Button - aligned with title on mobile */}
                <button
                  onClick={handleSaveToggle}
                  className={`relative group flex justify-center items-center lg:h-12 lg:w-12 h-10 w-10 rounded-lg border border-gray-200 hover:border-[#DB4F40] hover:bg-[#DB4F40]/5 transition-all duration-200 ${!user ? "opacity-50 cursor-not-allowed" : ""} lg:hidden`}
                  disabled={!user}
                >
                  {savedOpportunities.has(id) ? (
                    <FaHeart className="lg:h-6 lg:w-6 h-5 w-5 text-[#DB4F40]" />
                  ) : (
                    <FaRegHeart className="lg:h-6 lg:w-6 h-5 w-5 text-[#DB4F40]" />
                  )}
                  <div className="absolute -bottom-8 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-xs font-medium text-[#1B222A]/70">
                    {user ? "Save opportunity" : "Login to save"}
                  </div>
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {ageInfo && (
                  <span className="px-3 py-1 bg-[#5AC1E4]/20 text-[#1B222A] rounded-full text-xs font-medium">
                    {ageInfo.label}
                  </span>
                )}
                {open_to && (
                  <span className="px-3 py-1 bg-[#5AC1E4]/20 text-[#1B222A] rounded-full text-xs font-medium">
                    {open_to}
                  </span>
                )}
                {fully_funded && (
                  <span className="px-3 py-1 bg-[#5AC1E4]/20 text-[#1B222A] rounded-full text-xs font-medium flex items-center">
                    <FaCheckCircle className="mr-1 text-[#DB4F40]" />
                    Fully Funded
                  </span>
                )}
              </div>
            </div>

            {/* University and Location */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-4 text-base">
              <div className="flex items-center">
                <FaUniversity className="text-[#DB4F40] mr-2" />
                <span className="font-semibold text-[#6C757D] hover:text-[#495057] transition-colors">{university}</span>
              </div>
              <div className="flex items-center">
                <FaMapMarkerAlt className="text-[#DB4F40] mr-2" />
                <span className="font-semibold text-[#6C757D] hover:text-[#495057] transition-colors">{city}, {country}</span>
              </div>
            </div>

            {/* Key Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 text-base">
              {department && (
                <div className="flex items-center">
                  <IconWithTooltip
                    icon={FaGraduationCap}
                    label="Department"
                    color="text-[#DB4F40] mr-2"
                    className="h-5 w-5 flex-shrink-0"
                    position="left"
                  />
                  <span className="truncate font-semibold text-[#5D6D7E] hover:text-[#4F6D7A] transition-colors">{department}</span>
                </div>
              )}
              {supervisor && (
                <div className="flex items-center">
                  <IconWithTooltip
                    icon={FaUserTie}
                    label="Supervisor"
                    color="text-[#DB4F40] mr-2"
                    className="h-5 w-5 flex-shrink-0"
                    position="left"
                  />
                  <span className="truncate font-semibold text-[#5D6D7E] hover:text-[#4F6D7A] transition-colors">
                    {supervisor_url ? (
                      <a href={supervisor_url} className="hover:underline text-inherit" target="_blank" rel="noopener noreferrer">
                        {supervisor}
                      </a>
                    ) : (
                      supervisor
                    )}
                  </span>
                </div>
              )}
              {isValidClosingDate(closing_date) && (
                <div className="flex items-center">
                  <IconWithTooltip
                    icon={FaCalendarAlt}
                    label="Deadline"
                    color="text-[#DB4F40] mr-2"
                    className="h-5 w-5 flex-shrink-0"
                    position="left"
                  />
                  <span className="font-semibold text-[#5D6D7E] hover:text-[#4F6D7A] transition-colors">
                    {formatDate(closing_date)}
                  </span>
                </div>
              )}
            </div>

            {/* AI Summary */}
            {ai_summary && (
              <div className="mb-4">
                <p className="text-base text-[#1B222A]/80 leading-relaxed max-w-[85%]">
                  {ai_summary}
                </p>
                <div className="flex items-center mt-4 justify-between">
                  <button
                    onClick={handleClick}
                    className="group text-base text-white bg-[#DB4F40] hover:bg-[#C64537] font-medium transition-colors inline-flex items-center px-4 py-2 rounded-full shadow-md focus:outline-none focus:ring-2 focus:ring-[#DB4F40]/50"
                  >
                    More Details
                    <span className="ml-2 flex items-center transition-transform group-hover:translate-x-1">
                      {/* Dart/Arrow SVG */}
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10.293 15.707a1 1 0 010-1.414L13.586 11H4a1 1 0 110-2h9.586l-3.293-3.293a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" />
                      </svg>
                    </span>
                  </button>
                  
                  {/* Views Counter - aligned with More Details button on mobile */}
                  <div className="bg-[#DB4F40]/10 text-[#DB4F40] px-3 py-2 rounded-lg text-sm flex items-center shadow-sm border border-[#DB4F40]/20 lg:hidden">
                    <FaEye className="w-4 h-4 mr-2" />
                    <span className="font-medium">{opportunity.views || 0}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Right Side Actions - Heart and View Counter in same column (Desktop Only) */}
      <div className="absolute lg:top-4 lg:right-4 lg:bottom-4 top-2 right-2 bottom-2 flex flex-col items-center hidden lg:flex">
        {/* Heart Button - using design from OpportunityDetail.jsx */}
        <button
          onClick={handleSaveToggle}
          className={`relative group flex justify-center items-center lg:h-12 lg:w-12 h-10 w-10 rounded-lg border border-gray-200 hover:border-[#DB4F40] hover:bg-[#DB4F40]/5 transition-all duration-200 ${!user ? "opacity-50 cursor-not-allowed" : ""}`}
          disabled={!user}
        >
          {savedOpportunities.has(id) ? (
            <FaHeart className="lg:h-6 lg:w-6 h-5 w-5 text-[#DB4F40]" />
          ) : (
            <FaRegHeart className="lg:h-6 lg:w-6 h-5 w-5 text-[#DB4F40]" />
          )}
          <div className="absolute -bottom-8 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-xs font-medium text-[#1B222A]/70">
            {user ? "Save opportunity" : "Login to save"}
          </div>
        </button>
        
        {/* Views Counter - positioned to align with More Details button */}
        <div className="bg-[#DB4F40]/10 text-[#DB4F40] px-3 py-2 rounded-lg text-sm flex items-center shadow-sm border border-[#DB4F40]/20 mt-auto lg:mb-8 mb-4">
          <FaEye className="w-4 h-4 mr-2" />
          <span className="font-medium">{opportunity.views || 0}</span>
        </div>
      </div>
    </div>
  );
};

// Add helper functions for deadline validation and formatting
const isValidClosingDate = (timestamp) => {
  if (!timestamp) return false;
  const num = Number(timestamp);
  if (isNaN(num) || num <= 0) return false;
  const jan1970 = new Date(0).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const date = new Date(num * 1000);
  const formatted = date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  return formatted !== jan1970;
};

const formatDate = (timestamp) => {
  const num = Number(timestamp);
  const date = new Date(num * 1000);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Updated Grid component for opportunities with single column layout
const OpportunityGrid = ({
  opportunities,
  user,
  savedOpportunities,
  handleToggleSave,
  gridCols = "grid-cols-1", // Single column layout
  gap = "gap-6", // Reduced gap for single column
  customClassName = "", // Allow custom className override
}) => {
  // Use custom className if provided, otherwise use the gridCols and gap props
  const gridClassName = customClassName || `grid ${gridCols} ${gap} w-full`;
  
  return (
    <div className={gridClassName}>
      {opportunities.map((opportunity, idx) => (
        <OpportunityCard
          key={idx}
          opportunity={opportunity}
          user={user}
          savedOpportunities={savedOpportunities}
          handleToggleSave={handleToggleSave}
        />
      ))}
    </div>
  );
};

// Export both components
export default OpportunityGrid;
export { OpportunityCard };